<script setup>
import { computed } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: {
    type: String,
    required: true,
  },
  hasAppliedFilters: {
    type: Boolean,
    required: true,
  },
  hasActiveFolders: {
    type: Boolean,
    required: true,
  },
  activeFolderEditable: {
    type: Boolean,
    required: true,
  },
  activeStatus: {
    type: String,
    required: true,
  },
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
  showQuickSearch: {
    type: Boolean,
    default: false,
  },
  quickSearchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'cancelFilter',
  'basicFilterChange',
  'filtersModal',
  'advancedFiltersModal',
  'toggleQuickSearch',
  'quickSearchInput',
]);

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const onQuickSearchInputChange = debounce(event => {
  emit('quickSearchInput', event.target.value);
}, 500);

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex flex-col gap-2 px-4 pt-3 pb-2"
    :class="{
      'pb-3 border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center justify-center min-w-0">
        <h1
          class="text-lg font-medium truncate text-n-slate-12"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
        <span
          v-if="!hasAppliedFiltersOrActiveFolders"
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        >
          {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
        </span>
      </div>
      <div class="flex items-center gap-1">
        <NextButton
          v-tooltip.top-end="$t('CHAT_LIST.SEARCH_MESSAGES')"
          :icon="showQuickSearch ? 'i-lucide-x' : 'i-lucide-search'"
          slate
          xs
          faded
          @click="emit('toggleQuickSearch')"
        />
        <template v-if="hasAppliedFilters && hasActiveFolders">
          <div class="relative">
            <NextButton
              id="toggleAdvancedFilterButton"
              v-tooltip.top-end="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              xs
              faded
              @click="emit('advancedFiltersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CANCEL_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('cancelFilter')"
          />
        </template>
        <!-- Temporary filter applied from base view: show advanced filter, save and clear buttons -->
        <template v-else-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              id="toggleAdvancedFilterButton"
              v-tooltip.top-end="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              xs
              faded
              @click="emit('advancedFiltersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>
        <!-- Custom view without temporary filter: show edit/delete/advanced filter buttons -->
        <template
          v-else-if="
            hasActiveFolders && activeFolderEditable && !hasAppliedFilters
          "
        >
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>
        <!-- Custom view without temporary filter: show advanced filter button -->
        <template v-if="hasActiveFolders && !hasAppliedFilters">
          <div class="relative">
            <NextButton
              id="toggleAdvancedFilterButton"
              v-tooltip.top-end="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              xs
              faded
              @click="emit('advancedFiltersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-40 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
        </template>
        <div
          v-else-if="!hasActiveFolders && !hasAppliedFilters"
          class="relative"
        >
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
            icon="i-lucide-list-filter"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-40 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <ConversationBasicFilter
          v-if="!hasAppliedFiltersOrActiveFolders"
          :is-on-expanded-layout="isOnExpandedLayout"
          @change-filter="onBasicFilterChange"
        />
        <!-- Phones have room for one column only -->
        <SwitchLayout
          class="max-md:hidden"
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="toggleConversationLayout"
        />
      </div>
    </div>
    <!-- Quick search input field -->
    <div v-if="showQuickSearch" class="flex items-center gap-2">
      <div class="relative flex-1">
        <input
          :value="quickSearchQuery"
          type="text"
          :placeholder="$t('CHAT_LIST.SEARCH_INPUT_PLACEHOLDER')"
          class="!my-0 !py-1 !text-sm !h-auto"
          @input="onQuickSearchInputChange"
        />
      </div>
    </div>
  </div>
</template>
